import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import { countries as countryNames } from 'countries-list';

export interface CountryOption {
  value: CountryCode;
  label: string;
}

// countries-list's TCountries only covers ISO 3166 countries, while
// libphonenumber-js's CountryCode also includes a few numbering-plan-only
// territories (e.g. "AC", "TA") — cast to a permissive index so we can look
// up either safely and filter out the ones with no matching name below.
const countryNamesByCode = countryNames as unknown as Record<
  string,
  { name: string } | undefined
>;

// Every calling-code country libphonenumber-js knows, labelled with its
// display name from countries-list (already a repo dependency).
export const buildCountryOptions = (): CountryOption[] =>
  getCountries()
    .map((code): CountryOption | null => {
      const name = countryNamesByCode[code]?.name;
      return name
        ? { value: code, label: `${name} (+${getCountryCallingCode(code)})` }
        : null;
    })
    .filter((option): option is CountryOption => option !== null)
    .sort((a, b) => a.label.localeCompare(b.label));

export const getCountryFromValue = (value: string): CountryCode | undefined =>
  parsePhoneNumberFromString(value)?.country;

export const getNationalNumberFromValue = (value: string): string =>
  parsePhoneNumberFromString(value)?.nationalNumber ?? '';

// Live display formatting as the user types, e.g. "612345" -> "6 12 34 5".
export const formatNationalInput = (country: CountryCode, digits: string): string =>
  new AsYouType(country).input(digits);

// May return an incomplete E.164 string — callers validate with
// `isValidPhoneNumber` before treating it as a real number.
export const toE164 = (country: CountryCode, digits: string): string => {
  const formatter = new AsYouType(country);
  formatter.input(digits);
  return formatter.getNumber()?.number ?? '';
};
